import { computed, getCurrentInstance, ref } from "vue"

import { useProjects } from "@/store/projects"
import type { SearchEntry } from "@/composables/use-search"

/**
 * useSearchIndex
 *
 * Single source of truth for the in-memory search corpus (students +
 * programmes). Previously this index was assembled inline in Search.vue; both
 * the typeahead dropdown in SearchModal.vue and the full results panel in
 * Search.vue now share it so the entry shape stays in one place.
 *
 * The entry list is cached at module scope: students come from the global
 * projects store and programmes from `$site`, neither of which changes during a
 * session, so the corpus only needs building once. `ensureIndex()` is
 * idempotent — it resolves immediately once the cache is warm.
 */

let cached: SearchEntry[] | null = null
let inflight: Promise<SearchEntry[]> | null = null

const entries = ref<SearchEntry[]>(cached ?? [])

export function useSearchIndex() {
  const { visibleStudents, loadProjects } = useProjects()

  // `$site` is only reachable from within a component instance, so capture it
  // here (during setup) rather than inside the async builder.
  const instance = getCurrentInstance()
  const site = instance?.appContext.config.globalProperties.$site

  function buildProgrammeEntries(): SearchEntry[] {
    return (site?.programmeGroups ?? []).flatMap((group: any) =>
      group.courses.map((course: any) => ({
        id: course.id,
        uri: course.uri,
        title: undefined,
        name: undefined,
        programme: course.title,
      })),
    )
  }

  function buildStudentEntries(): SearchEntry[] {
    return visibleStudents.value.map(student => ({
      id: student.id,
      uri: student.uri,
      title: student.title,
      name: student.name,
      programme: undefined,
    }))
  }

  async function ensureIndex(): Promise<SearchEntry[]> {
    if (cached) return cached
    if (inflight) return inflight

    inflight = (async () => {
      // Resolves immediately if projects are already loaded.
      await loadProjects()
      cached = [...buildStudentEntries(), ...buildProgrammeEntries()]
      entries.value = cached
      inflight = null
      return cached
    })()

    return inflight
  }

  return {
    entries: computed(() => entries.value),
    ready: computed(() => entries.value.length > 0),
    ensureIndex,
  }
}
